import { type FunctionComponent, useState } from 'react';

import { Button, Input, Modal } from '@dr.pogodin/react-utils';
import { captureException } from '@sentry/react';

import Throbber from 'components/Throbber';

import { createResearchLibCategory } from 'services/researchLib';

import { updateResearchLibCategory } from 'services/researchLib/updateCategory';
import { type ResearchLibCategoryT, getId, useGlobalStateObject } from 'utils';

import secondaryButton from 'themes/Button/admin/secondary-v2.scss';

import './style.scss';
import inputTheme from 'themes/Input/admin/with-inner-label-60px.scss';

type PropsT = {
  initial: ResearchLibCategoryT | null;
  onClose: () => void;
};

const EditCategoryModal: FunctionComponent<PropsT> = ({
  initial,
  onClose,
}) => {
  const [busy, setBusy] = useState(false);

  const [catName, setCatName] = useState<string>(initial?.name ?? '');

  const gs = useGlobalStateObject();

  return (
    <Modal
      onCancel={busy ? undefined : onClose}
    >
      <div styleName="content">
        <h1 styleName="header">Add New Category</h1>
        <Input
          label="Name"
          onChange={(e) => {
            setCatName(e.target.value);
          }}
          theme={inputTheme}
          value={catName}
        />
        <div styleName="buttons">
          <Button
            onClick={onClose}
            theme={secondaryButton}
          >
            Cancel
          </Button>
          <Button
            disabled={!catName}
            onClick={() => {
              void (async () => {
                try {
                  setBusy(true);

                  if (initial) {
                    const catId = getId(initial);

                    const res = await updateResearchLibCategory({
                      id: catId,
                      name: catName,
                    });

                    let list = gs.get('researchLib.categories.data');
                    if (!list) throw Error('Internal error');

                    list = [...list];
                    const idx = list.findIndex((item) => item.id === catId);
                    if (idx < 0) throw Error('Internal error');

                    list[idx] = res;
                    gs.set('researchLib.categories.data', list);
                  } else {
                    const res = await createResearchLibCategory({
                      name: catName,
                    });

                    let list = gs.get('researchLib.categories.data') ?? [];
                    list = [...list, res];
                    list.sort((a, b) => a.name.localeCompare(b.name));
                    gs.set('researchLib.categories.data', list);
                  }

                  onClose();
                } catch (error) {
                  const action = initial ? 'update' : 'create';

                  captureException(error);
                  // eslint-disable-next-line no-alert
                  alert(`Failed to ${action} the category`);
                } finally {
                  setBusy(false);
                }
              })();
            }}
          >
            {initial ? 'Update' : 'Create'}
          </Button>
        </div>
        {busy ? <Throbber withOverlay /> : null}
      </div>
    </Modal>
  );
};

export default EditCategoryModal;
